import React, { useState } from 'react';
import Footer from '../components/Footer';
import useScrollReveal from '../components/useScrollReveal';
import './Prayer.css';

function Reveal({ children, delay = '0s', className = '' }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} className={`reveal-block ${className}`} style={{ '--reveal-delay': delay }}>
      {children}
    </div>
  );
}

const prayers = [
  {
    id: 'golden-arrow',
    title: 'The Golden Arrow',
    origin: 'Given to Sister Marie de Saint-Pierre, 1844',
    text: `May the most holy, most sacred, most adorable,
most incomprehensible and unutterable Name of God
be always praised, blessed, loved, adored
and glorified in Heaven, on earth,
and under the earth, by all the creatures of God,
and by the Sacred Heart of Our Lord Jesus Christ
in the Most Holy Sacrament of the Altar. Amen.`,
  },
  {
    id: 'reparation',
    title: 'Act of Reparation to the Holy Face',
    origin: 'Traditional',
    text: `O Jesus, who in Thy bitter Passion didst become
"the most abject of men, a man of sorrows,"
I venerate Thy Sacred Face whereon there once did shine
the beauty and sweetness of the Godhead;
but now it has become for me
as if it were the face of a leper!

Nevertheless, under those disfigured features,
I recognize Thy infinite love,
and I am consumed with the desire to love Thee
and to make Thee loved by all men.
The tears which well up abundantly in Thy sacred eyes
appear to me as so many pearls that I love to gather up,
in order to purchase the souls of poor sinners
by means of their infinite value. Amen.`,
  },
  {
    id: 'therese',
    title: 'Prayer of Saint Thérèse to the Holy Face',
    origin: 'Saint Thérèse of Lisieux',
    text: `O Jesus, who in Thy bitter Passion
didst imprint Thy adorable Face upon the veil of Veronica,
deign to imprint it also upon my soul,
so that I may live only for Thee
and that I may appear before Thee
adorned with the marks of Thy holy passion
at the hour of my death. Amen.`,
  },
];

export default function Prayer() {
  const [active, setActive] = useState('golden-arrow');
  const current = prayers.find(p => p.id === active);

  return (
    <div className="page page-dark">
      {/* Hero */}
      <section className="prayer-hero">
        <div className="prayer-hero__glow" />
        <div className="prayer-hero__content fade-up">
          <span className="section-label light">Sacred Prayers</span>
          <h1>Prayers to the<br />Holy Face</h1>
          <p>Words of reparation, love, and adoration</p>
        </div>
      </section>

      <div className="section-inner">
        {/* Intro */}
        <Reveal>
          <p className="prayer-intro">
            The devotion to the Holy Face of Jesus is centered on reparation — offering
            prayers and acts of love to console the heart of Christ wounded by sin,
            blasphemy, and indifference. The prayers below have been handed down through
            the saints and approved by the Church.
          </p>
        </Reveal>

        {/* Interactive Prayer Widget */}
        <Reveal delay="0.15s">
          <div className="prayer-widget">
            <div className="prayer-tabs">
              {prayers.map(p => (
                <button
                  key={p.id}
                  className={`prayer-tab ${active === p.id ? 'prayer-tab--active' : ''}`}
                  onClick={() => setActive(p.id)}
                >
                  {p.title}
                </button>
              ))}
            </div>
            <div className="prayer-body" key={active}>
              <p className="prayer-origin">{current.origin}</p>
              <div className="prayer-text">
                {current.text.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < current.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
              <div className="prayer-medal">
                <div className="medal-ring"><span>✦</span></div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* How to pray section */}
        <Reveal delay="0.1s">
          <h2 className="how-title">How to Pray the Holy Face Devotion</h2>
        </Reveal>
        <div className="steps">
          {[
            {
              num: '01',
              title: 'Gaze Upon the Face',
              body: 'Begin by spending a few moments looking upon an image of the Holy Face. Let the gaze of love pass between you and Our Lord.',
            },
            {
              num: '02',
              title: 'Act of Contrition',
              body: 'Offer an act of sorrow for your sins and for the sins of all mankind that wound the Face of Christ.',
            },
            {
              num: '03',
              title: 'Pray the Golden Arrow',
              body: 'Recite the Golden Arrow prayer as reparation for blasphemy — turning the wound of sin into a golden arrow of love.',
            },
            {
              num: '04',
              title: 'Meditate on His Suffering',
              body: 'Reflect on the face of Christ in His Passion — spat upon, crowned with thorns, disfigured — and unite your own sufferings to His.',
            },
          ].map(({ num, title, body }, i) => (
            <Reveal key={num} delay={`${i * 0.1}s`}>
              <div className="step">
                <span className="step__num">{num}</span>
                <div className="step__content">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Closing scripture */}
      <div className="prayer-close">
        <Reveal>
          <blockquote className="closing-verse">
            <p>"Look to him and be radiant, so your faces shall never be ashamed."</p>
            <cite>— Psalm 34:5</cite>
          </blockquote>
        </Reveal>
      </div>

      <Footer />
    </div>
  );
}
